import { supabase } from '@/integrations/supabase/client';

export interface BotMappingCsvRow {
  PARENT_BOT_IDENTIFIER: string;
  CHILD_BOT_IDENTIFIER: string;
  IS_ACTIVE: string;
  NOTES?: string;
  ENDPOINT_NAME?: string;
  ENDPOINT_URL?: string;
  ENDPOINT_ACTIVE?: string;
  ENDPOINT_TIMEOUT_MS?: string;
}

export interface ParsedMappingRow {
  parentBotId: string;
  childBotId: string;
  isActive: boolean;
  notes?: string;
  endpoints?: {
    name: string;
    url: string;
    isActive: boolean;
    timeoutMs: number;
  }[];
  errors: string[];
}

export interface ImportResult {
  success: boolean;
  imported: number;
  failed: number;
  errors: { row: number; message: string }[];
}

// Find bot by identifier with priority: ID > Signal Token > Name
export const findBotByIdentifier = async (identifier: string): Promise<string | null> => {
  if (!identifier?.trim()) return null;
  
  const cleanId = identifier.trim();
  
  // Try by exact ID first (UUID format)
  if (cleanId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
    const { data } = await supabase
      .from('bots')
      .select('id')
      .eq('id', cleanId)
      .single();
    if (data) return data.id;
  }
  
  // Try by signal token
  const { data: tokenData } = await supabase
    .from('bots')
    .select('id')
    .eq('signal_token', cleanId)
    .single();
  if (tokenData) return tokenData.id;
  
  // Try by name (case insensitive)
  const { data: nameData } = await supabase
    .from('bots')
    .select('id')
    .ilike('name', cleanId)
    .single();
  if (nameData) return nameData.id;
  
  return null;
};

// Parse CSV content
export const parseBotMappingCsv = (csvContent: string): BotMappingCsvRow[] => {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) throw new Error('CSV must have at least header and one data row');
  
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
  const requiredHeaders = ['PARENT_BOT_IDENTIFIER', 'CHILD_BOT_IDENTIFIER', 'IS_ACTIVE'];
  
  for (const required of requiredHeaders) {
    if (!headers.includes(required)) {
      throw new Error(`Missing required column: ${required}`);
    }
  }
  
  const rows: BotMappingCsvRow[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
    if (values.length === 1 && values[0] === '') continue; // Skip empty rows
    
    const row: any = {};
    headers.forEach((header, index) => {
      row[header] = values[index] || '';
    });
    
    rows.push(row as BotMappingCsvRow);
  }
  
  return rows;
};

// Validate and parse a single mapping row
export const validateMappingRow = async (
  row: BotMappingCsvRow, 
  rowIndex: number
): Promise<ParsedMappingRow> => {
  const errors: string[] = [];
  const result: ParsedMappingRow = {
    parentBotId: '',
    childBotId: '',
    isActive: false,
    errors: []
  };
  
  // Validate parent bot
  if (!row.PARENT_BOT_IDENTIFIER?.trim()) {
    errors.push('Parent bot identifier is required');
  } else {
    const parentId = await findBotByIdentifier(row.PARENT_BOT_IDENTIFIER);
    if (!parentId) {
      errors.push(`Parent bot not found: ${row.PARENT_BOT_IDENTIFIER}`);
    } else {
      result.parentBotId = parentId;
    }
  }
  
  // Validate child bot
  if (!row.CHILD_BOT_IDENTIFIER?.trim()) {
    errors.push('Child bot identifier is required');
  } else {
    const childId = await findBotByIdentifier(row.CHILD_BOT_IDENTIFIER);
    if (!childId) {
      errors.push(`Child bot not found: ${row.CHILD_BOT_IDENTIFIER}`);
    } else {
      result.childBotId = childId;
    }
  }
  
  // Validate same bot check
  if (result.parentBotId && result.childBotId && result.parentBotId === result.childBotId) {
    errors.push('Parent and child cannot be the same bot');
  }
  
  // Validate IS_ACTIVE
  const activeValue = row.IS_ACTIVE?.toLowerCase().trim();
  if (!['true', 'false', '1', '0', 'yes', 'no'].includes(activeValue)) {
    errors.push('IS_ACTIVE must be true/false, 1/0, or yes/no');
  } else {
    result.isActive = ['true', '1', 'yes'].includes(activeValue);
  }
  
  // Parse notes
  if (row.NOTES?.trim()) {
    result.notes = row.NOTES.trim();
  }
  
  // Parse endpoints if provided
  if (row.ENDPOINT_NAME?.trim() && row.ENDPOINT_URL?.trim()) {
    const endpointActive = row.ENDPOINT_ACTIVE?.toLowerCase().trim();
    const timeoutMs = parseInt(row.ENDPOINT_TIMEOUT_MS || '5000');
    
    if (!['true', 'false', '1', '0', 'yes', 'no'].includes(endpointActive || 'true')) {
      errors.push('ENDPOINT_ACTIVE must be true/false, 1/0, or yes/no');
    }
    
    if (isNaN(timeoutMs) || timeoutMs < 1000 || timeoutMs > 30000) {
      errors.push('ENDPOINT_TIMEOUT_MS must be between 1000 and 30000');
    }
    
    try {
      new URL(row.ENDPOINT_URL);
      result.endpoints = [{
        name: row.ENDPOINT_NAME.trim(),
        url: row.ENDPOINT_URL.trim(),
        isActive: ['true', '1', 'yes'].includes(endpointActive || 'true'),
        timeoutMs
      }];
    } catch {
      errors.push('Invalid ENDPOINT_URL format');
    }
  }
  
  result.errors = errors;
  return result;
};

// Check for duplicate mappings
export const checkDuplicateMapping = async (parentBotId: string, childBotId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('bot_merge_mapping')
    .select('id')
    .eq('parent_bot_id', parentBotId)
    .eq('child_bot_id', childBotId)
    .single();
  
  return !!data;
};

// Import bot mappings in bulk
export const importBotMappings = async (
  rows: ParsedMappingRow[],
  skipDuplicates: boolean = true
): Promise<ImportResult> => {
  const result: ImportResult = {
    success: true,
    imported: 0,
    failed: 0,
    errors: []
  };
  
  const user = await supabase.auth.getUser();
  const userId = user.data.user?.id;
  
  if (!userId) {
    throw new Error('User not authenticated');
  }
  
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    
    if (row.errors.length > 0) {
      result.failed++;
      result.errors.push({
        row: i + 2, // +2 for header and 0-based index
        message: row.errors.join('; ')
      });
      continue;
    }
    
    try {
      // Check for duplicates
      const isDuplicate = await checkDuplicateMapping(row.parentBotId, row.childBotId);
      if (isDuplicate) {
        if (skipDuplicates) {
          result.failed++;
          result.errors.push({
            row: i + 2,
            message: 'Duplicate mapping (skipped)'
          });
          continue;
        } else {
          // Update existing mapping
          const { data: existingMapping } = await supabase
            .from('bot_merge_mapping')
            .select('id')
            .eq('parent_bot_id', row.parentBotId)
            .eq('child_bot_id', row.childBotId)
            .single();
          
          if (existingMapping) {
            await supabase
              .from('bot_merge_mapping')
              .update({
                is_active: row.isActive,
                notes: row.notes
              })
              .eq('id', existingMapping.id);
          }
        }
      } else {
        // Create new mapping
        const { data: newMapping, error: mappingError } = await supabase
          .from('bot_merge_mapping')
          .insert({
            parent_bot_id: row.parentBotId,
            child_bot_id: row.childBotId,
            is_active: row.isActive,
            notes: row.notes,
            created_by: userId
          })
          .select('id')
          .single();
        
        if (mappingError) throw mappingError;
        
        // Create endpoints if provided
        if (row.endpoints && row.endpoints.length > 0 && newMapping) {
          for (const endpoint of row.endpoints) {
            await supabase
              .from('bot_signal_forward_endpoints')
              .insert({
                mapping_id: newMapping.id,
                bot_id: row.parentBotId,
                name: endpoint.name,
                url: endpoint.url,
                is_active: endpoint.isActive,
                timeout_ms: endpoint.timeoutMs,
                created_by: userId
              });
          }
        }
      }
      
      result.imported++;
    } catch (error: any) {
      result.failed++;
      result.errors.push({
        row: i + 2,
        message: error.message || 'Unknown error occurred'
      });
    }
  }
  
  if (result.failed > 0) {
    result.success = false;
  }
  
  return result;
};

// Generate CSV template
export const generateCsvTemplate = (): string => {
  const headers = [
    'PARENT_BOT_IDENTIFIER',
    'CHILD_BOT_IDENTIFIER', 
    'IS_ACTIVE',
    'NOTES',
    'ENDPOINT_NAME',
    'ENDPOINT_URL',
    'ENDPOINT_ACTIVE',
    'ENDPOINT_TIMEOUT_MS'
  ];
  
  const sampleRows = [
    [
      'parent-bot-signal-token-123',
      'child-bot-uuid-456',
      'true',
      'Production mapping for EURUSD signals',
      'Primary Webhook',
      'https://api.example.com/webhook/signals',
      'true',
      '5000'
    ],
    [
      'ParentBotName',
      'ChildBotName',
      'false',
      'Test mapping - disabled',
      'Backup Webhook',
      'https://backup.example.com/webhook',
      'false',
      '3000'
    ],
    [
      '12345678-1234-1234-1234-123456789abc',
      'another-signal-token',
      'true',
      'Cross-symbol mapping',
      '',
      '',
      '',
      ''
    ]
  ];
  
  const csvContent = [
    headers.join(','),
    ...sampleRows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  return csvContent;
};